import React from "react";
import { Link } from "react-router-dom";
import {
  FaWallet,
  FaRupeeSign,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaCalendarAlt,
} from "react-icons/fa";

const WalletSummaryCards = ({ walletSummary, isLoading }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {[...Array(6)].map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-xl shadow-md p-6 border border-gray-100 animate-pulse"
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              <div className="ml-4 flex-1">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-6 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!walletSummary) {
    return null;
  }


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Calculate commission from wallet data if backend returns 0
  const calculateCommissionFromWallets = () => {
    if (!walletSummary.wallets || !Array.isArray(walletSummary.wallets)) return 0;

    return walletSummary.wallets.reduce((total, wallet) => {
      return total + (wallet.totalCommissionCut || 0);
    }, 0);
  };

  // Get actual commission value with fallbacks
  const getActualCommission = () => {
    const backendValue = walletSummary.totalCommissionEarned || walletSummary.totalCommission || walletSummary.totalCommissionCut || 0;
    if (backendValue > 0) return backendValue;

    // Fallback: calculate from wallet data
    const calculatedValue = calculateCommissionFromWallets();
    return calculatedValue;
  };

  const cards = [
    {
      title: "Total Commission Earned",
      value: formatCurrency(getActualCommission()),
      icon: FaRupeeSign,
      bgColor: "bg-green-100",
      textColor: "text-green-800",
      link: "/admin/wallet/transactions?type=commission_deduction",
      subtitle: "All time commission",
    },
    {
      title: "Commission Today",
      value: formatCurrency(walletSummary.commissionToday || walletSummary.todayCommission || 0),
      icon: FaCalendarAlt,
      bgColor: "bg-blue-100",
      textColor: "text-blue-800",
      link: "/admin/wallet/transactions?type=commission_deduction&date=today",
      subtitle: "Today's earnings",
    },
    {
      title: "Commission This Month",
      value: formatCurrency(walletSummary.commissionThisMonth || walletSummary.monthlyCommission || 0),
      icon: FaClock,
      bgColor: "bg-purple-100",
      textColor: "text-purple-800",
      link: "/admin/wallet/transactions?type=commission_deduction&date=month",
      subtitle: "Monthly commission",
    },
    {
      title: "Vendor Commission Balance",
      value: formatCurrency(walletSummary.totalVendorCommissionBalance || walletSummary.totalVendorCommissions || 0),
      icon: FaRupeeSign,
      bgColor: "bg-emerald-100",
      textColor: "text-emerald-800",
      link: "/admin/wallet/transactions?type=vendor_commission_credited",
      subtitle: "All vendors total",
    },
    {
      title: "Driver Wallet Balance",
      value: formatCurrency(walletSummary.totalDriverWalletBalance || walletSummary.totalPositiveBalance || 0),
      icon: FaWallet,
      bgColor: "bg-indigo-100",
      textColor: "text-indigo-800",
      link: "/admin/wallet/drivers?filter=positive",
      subtitle: "Total positive balances",
    },
    {
      title: "Drivers with Dues",
      value: walletSummary.driversWithNegativeWallet || walletSummary.negativeBalanceDrivers || 0,
      icon: FaExclamationTriangle,
      bgColor: "bg-red-100",
      textColor: "text-red-800",
      link: "/admin/wallet/drivers?filter=negative",
      subtitle: `${formatCurrency(Math.abs(walletSummary.totalNegativeBalance || 0))} total due`,
      showCount: true,
    },
    {
      title: "Withdrawal Requests",
      value: walletSummary.driversWithWithdrawalRequests || walletSummary.pendingWithdrawals || 0,
      icon: FaCheckCircle,
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
      link: "/admin/wallet/withdrawals",
      subtitle: "Pending approvals",
      showCount: true,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
      {cards.map((card, index) => (
        <Link
          key={index}
          to={card.link}
          className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 block"
        >
          <div className="flex items-center">
            <div
              className={`p-3 rounded-full ${card.bgColor} ${card.textColor}`}
            >
              <card.icon className="w-6 h-6" />
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-gray-600 text-sm font-medium">
                {card.title}
              </h3>
              <p className="text-2xl font-bold text-gray-900">
                {card.showCount ? card.value : card.value}
              </p>
              <p className="text-xs text-gray-500 mt-1">{card.subtitle}</p>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default WalletSummaryCards;
